/**
 * Thin wrapper over html2canvas for the in-app bug reporter's "attach
 * screenshot" affordance. html2canvas is lazy-imported so it never lands in
 * the main bundle; the screenshot is downscaled + JPEG-compressed so the
 * preview stays small. Capture is always optional — any failure (tainted
 * canvas, unsupported CSS, timeout) resolves to null rather than throwing.
 */

const MAX_WIDTH = 1280;
const JPEG_QUALITY = 0.6;

/** Pure scale math, split out for testing without a DOM/canvas. */
export function computeScaledDimensions(
  width: number,
  height: number,
  maxWidth: number,
): { width: number; height: number; scale: number } {
  const scale = width > maxWidth ? maxWidth / width : 1;
  return { width: Math.round(width * scale), height: Math.round(height * scale), scale };
}

/** Capture `target` (default: the whole page) as a downsized JPEG data URL, or null on failure. */
export async function captureScreenshot(target?: HTMLElement): Promise<string | null> {
  try {
    const { default: html2canvas } = await import('html2canvas');
    const canvas = await html2canvas(target ?? document.body, {
      logging: false,
      useCORS: true,
      scale: 1,
    });

    const { width, height, scale } = computeScaledDimensions(canvas.width, canvas.height, MAX_WIDTH);
    if (scale === 1) {
      return canvas.toDataURL('image/jpeg', JPEG_QUALITY);
    }

    const scaledCanvas = document.createElement('canvas');
    scaledCanvas.width = width;
    scaledCanvas.height = height;
    const ctx = scaledCanvas.getContext('2d');
    if (!ctx) return canvas.toDataURL('image/jpeg', JPEG_QUALITY);

    ctx.drawImage(canvas, 0, 0, width, height);
    return scaledCanvas.toDataURL('image/jpeg', JPEG_QUALITY);
  } catch (err) {
    console.warn('captureScreenshot: capture failed, continuing without a screenshot:', err);
    return null;
  }
}
